import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Eye,
  Shield,
  UserCheck,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import usuariosService from '../services/usuariosService'
import LoadingSpinner from '../components/LoadingSpinner'

const GestionUsuarios = () => {
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPermiso, setFilterPermiso] = useState('')
  const [permisosDisponibles, setPermisosDisponibles] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [usuarioToDelete, setUsuarioToDelete] = useState(null)
  const [sortField, setSortField] = useState('id')
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    cargarUsuarios()
    cargarPermisosDisponibles()
  }, [])

  const cargarUsuarios = async () => {
    try {
      setLoading(true)
      const response = await usuariosService.getUsuarios()
      setUsuarios(response.data || [])
      setError(null)
    } catch (error) {
      console.error('Error cargando usuarios:', error)
      setError('Error al cargar los usuarios')
    } finally {
      setLoading(false)
    }
  }

  const cargarPermisosDisponibles = async () => {
    try {
      const response = await usuariosService.getPermisosDisponibles()
      setPermisosDisponibles(response.data || [])
    } catch (error) {
      console.error('Error cargando permisos:', error)
    }
  }

  const handleDelete = (usuario) => {
    setUsuarioToDelete(usuario)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    try {
      await usuariosService.deleteUsuario(usuarioToDelete.id)
      await cargarUsuarios()
      setShowDeleteModal(false)
      setUsuarioToDelete(null)
    } catch (error) {
      console.error('Error eliminando usuario:', error)
      setError('Error al eliminar el usuario')
    }
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? '↑' : '↓'
  }

  const filteredAndSortedUsuarios = usuarios
    .filter(usuario => {
      const matchesSearch = usuario.usuario.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesFilter = !filterPermiso || usuario.permisos.includes(filterPermiso)
      return matchesSearch && matchesFilter
    })
    .sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]
      
      if (sortField === 'permisos') {
        aValue = a.permisos.length
        bValue = b.permisos.length
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

  const getPermisosDisplay = (permisos) => {
    if (!permisos || permisos.length === 0) {
      return <span className="text-gray-500 text-sm">Sin permisos</span>
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {permisos.slice(0, 3).map((permiso, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
          >
            {permiso}
          </span>
        ))}
        {permisos.length > 3 && (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
            +{permisos.length - 3} más
          </span>
        )}
      </div>
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
            <p className="text-gray-600">Administra usuarios y permisos del sistema</p>
          </div>
        </div>
        
        {hasPermission('AGREGAR_USUARIO') && (
          <button
            onClick={() => navigate('/usuarios/nuevo')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuevo Usuario
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar Usuario
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar por nombre de usuario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por Permiso
            </label>
            <select
              value={filterPermiso}
              onChange={(e) => setFilterPermiso(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los permisos</option>
              {permisosDisponibles.map((permiso) => (
                <option key={permiso} value={permiso}>
                  {permiso}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('')
                setFilterPermiso('')
              }}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('id')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    ID {getSortIcon('id')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('usuario')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Usuario {getSortIcon('usuario')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('permisos')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Permisos {getSortIcon('permisos')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedUsuarios.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {usuario.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-900">
                        {usuario.usuario}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getPermisosDisplay(usuario.permisos)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      {hasPermission('VER_USUARIOS') && (
                        <button
                          onClick={() => navigate(`/usuarios/ver/${usuario.id}`)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      
                      {hasPermission('EDITAR_USUARIO') && (
                        <button
                          onClick={() => navigate(`/usuarios/editar/${usuario.id}`)}
                          className="text-yellow-600 hover:text-yellow-900 p-1"
                          title="Editar usuario"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      
                      {hasPermission('ELIMINAR_USUARIO') && usuario.id !== user?.id && (
                        <button
                          onClick={() => handleDelete(usuario)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Eliminar usuario"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredAndSortedUsuarios.length === 0 && (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No se encontraron usuarios</p>
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmar Eliminación
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              ¿Está seguro de que desea eliminar el usuario "{usuarioToDelete?.usuario}"? 
              Esta acción no se puede deshacer.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionUsuarios 